import { FC } from "react"
import { Stack } from "@mui/material"
import {
  TextFieldElement,
  SwitchElement,
  SelectElement,
  Controller,
} from "react-hook-form-mui"
import { RtkHookForm, SecretField } from "@/core/components"
import { Userbot } from "../../interfaces"
import { useCreateProxyMutation, useUpdateProxyMutation } from "../../store"

interface IProps {
  proxy?: Userbot.Proxy
  onDone?: () => void
}

interface IFormData {
  label?: string
  type: "socks5"
  host: string
  port: number | string
  username?: string
  password?: string
  isActive: boolean
  /** внутренний три-стейт; в API маппится в useIpv6: null | true | false */
  useIpv6: "auto" | "v6" | "v4"
}

const toUseIpv6Value = (useIpv6: boolean | null | undefined) =>
  useIpv6 == null ? "auto" : useIpv6 ? "v6" : "v4"

const toBody = (data: IFormData): Userbot.CreateProxy => ({
  label: data.label || undefined,
  type: data.type ?? "socks5",
  host: data.host,
  port: Number(data.port),
  username: data.username || undefined,
  password: data.password || undefined,
  isActive: data.isActive,
  useIpv6: data.useIpv6 === "auto" ? null : data.useIpv6 === "v6",
})

export const ProxyForm: FC<IProps> = ({ proxy, onDone }) => {
  const isEdit = !!proxy

  const defaultValues: IFormData = {
    label: proxy?.label ?? "",
    type: proxy?.type ?? "socks5",
    host: proxy?.host ?? "",
    port: proxy?.port ?? 1080,
    username: proxy?.username ?? "",
    password: proxy?.password ?? "",
    isActive: proxy?.isActive ?? true,
    useIpv6: toUseIpv6Value(proxy?.useIpv6),
  }

  return (
    <RtkHookForm<IFormData, Userbot.Proxy>
      successMessage={isEdit ? "Прокси обновлён" : "Прокси создан"}
      mutationHook={
        (isEdit ? useUpdateProxyMutation : useCreateProxyMutation) as never
      }
      getSubmitHandlerParams={(data) =>
        isEdit ? { id: proxy!.id, body: toBody(data) } : { body: toBody(data) }
      }
      afterSubmit={() => onDone?.()}
      formContextParams={{ defaultValues }}
      actions={[
        {
          text: "Сохранить",
          type: "submit",
          variant: "contained",
          fullWidth: true,
        },
      ]}
    >
      <Stack spacing={2}>
        <TextFieldElement<IFormData> name="label" label="Метка" />
        <SelectElement<IFormData>
          name="type"
          label="Тип"
          options={[{ id: "socks5", label: "socks5" }]}
          required
        />
        <SelectElement<IFormData>
          name="useIpv6"
          label="IPv6 выход"
          options={[
            { id: "auto", label: "Авто (по host)" },
            { id: "v6", label: "IPv6" },
            { id: "v4", label: "IPv4" },
          ]}
        />
        <TextFieldElement<IFormData> name="host" label="Host" required />
        <TextFieldElement<IFormData>
          name="port"
          label="Port"
          type="number"
          required
          validation={{
            min: { value: 1, message: "1..65535" },
            max: { value: 65535, message: "1..65535" },
          }}
        />
        <TextFieldElement<IFormData> name="username" label="Username" />
        <Controller<IFormData, "password">
          name="password"
          render={({ field, fieldState }) => (
            <SecretField
              label="Пароль"
              value={(field.value as string) ?? ""}
              onChange={field.onChange}
              fullWidth
              error={fieldState.error?.message}
            />
          )}
        />
        <SwitchElement<IFormData> name="isActive" label="Активен" />
      </Stack>
    </RtkHookForm>
  )
}
